/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Col, Row, Tooltip, Typography } from 'antd';

// Misc imports
import AnalyticsTable from '../Tables/AnalyticsTable';
import { Link, useParams } from 'react-router-dom';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import { NAV_BOT } from '../../constants/navigation';
import { ACTIVITY_TYPES } from '../../constants/main';
import exportAnalyticsData from '../../utils/misc/exportAnalyticsData';
import { DownloadOutlined } from '@ant-design/icons';

const UserFeedbackPerResponseSection = ({ data = [], sectionTitle = '', isLoading = true, renderOnlyTable = false }) => {
  const { t } = useTranslation();
  const { orgId } = useParams();

  const [sortedInfo, setSortedInfo] = useState({ order: '', columnKey: '' });

  const handleTableChange = (_pagination, _filters, sorter) => {
    setSortedInfo(sorter);
  };

  const tableConfig = {
    tableData: data?.map((value) => {
      return {
        ...value,
        ...value?.feedback
      };
    }),
    isDescriptionTable: false,
    isTableLoading: isLoading,
    emptyLines: 0,
    noScroll: data?.length < 8,
    scroll: data?.length > 8 ? 518 : null,
    tableColumns: [
      {
        title: t('Agent Response'),
        dataIndex: 'botResponse',
        key: 'botResponse',
        width: '30%',
        align: 'left',
        link: true,
        ellipsis: true,
        sorter: (a, b) => a.botResponse.localeCompare(b.botResponse),
        sortOrder: sortedInfo.columnKey === 'botResponse' && sortedInfo.order,
        render: (record, data) => {
          if (data.type === ACTIVITY_TYPES.AUTOMATED_ANSWERS) {
            return (
              <Link
                to={replaceUrlParams(NAV_BOT, { tenantId: data.tenantId, orgId }) + '?view=behavior&tab=automated-answers'}
                target="_blank"
                rel="noopener noreferrer"
              >
                {record}
              </Link>
            );
          }

          return (
            <Link
              to={replaceUrlParams(NAV_BOT, { tenantId: data.tenantId, orgId }) + `?view=behavior&tab=activities&activity=${data?.id}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {record}
            </Link>
          );
        }
      }, {
        title: t('Total'),
        dataIndex: 'total',
        key: 'total',
        width: '15%',
        align: 'center',
        ellipsis: true,
        sorter: (a, b) => a.total - b.total,
        sortOrder: sortedInfo.columnKey === 'total' && sortedInfo.order
      },
      {
        title: t('Positive'),
        dataIndex: 'positive',
        key: 'positive',
        width: '15%',
        align: 'center',
        ellipsis: true,
        sorter: (a, b) => a.positive - b.positive,
        sortOrder: sortedInfo.columnKey === 'positive' && sortedInfo.order
      },
      {
        title: t('Negative'),
        dataIndex: 'negative',
        key: 'negative',
        width: '15%',
        align: 'center',
        ellipsis: true,
        sorter: (a, b) => a.negative - b.negative,
        sortOrder: sortedInfo.columnKey === 'negative' && sortedInfo.order
      },
      {
        title: '% ' + t('Positive'),
        dataIndex: 'positivePercentage',
        key: 'positivePercentage',
        width: '25%',
        align: 'center',
        ellipsis: true,
        sorter: (a, b) => a.positivePercentage - b.positivePercentage,
        sortOrder: sortedInfo.columnKey === 'positivePercentage' && sortedInfo.order,
        render: (record) => {
          const fixedValue = Number(Number(record).toFixed(2));
          return `${fixedValue}%`;
        }
      }
    ]
  };

  return (
    <Fragment>
      {
        renderOnlyTable ? (
          <AnalyticsTable
            tableData={tableConfig.tableData}
            isDescriptionTable={tableConfig.isDescriptionTable}
            isTableLoading={tableConfig.isTableLoading}
            emptyLines={tableConfig.emptyLines}
            tableColumns={tableConfig.tableColumns}
            noScroll={tableConfig.noScroll}
            onChange={handleTableChange}
          />
        ) : (
          <Fragment>
            <Row justify="space-between">
              <Col className="mb-6 position-relative">
                <Typography.Title level={4} className="mb-6">
                  <Typography.Text className="mr-2">{sectionTitle}</Typography.Text>
                </Typography.Title>
              </Col>
              <Col>
                <Tooltip title={tableConfig.tableData.length === 0 ? t('There are no analytics to export') : t('Export as CSV')}>
                  <Button
                    type="text"
                    size="large"
                    onClick={() => exportAnalyticsData(tableConfig, 'user_feedback', sectionTitle)}
                    loading={tableConfig.isTableLoading}
                    disabled={tableConfig.tableData.length === 0}
                    icon={<DownloadOutlined />}
                  />
                </Tooltip>
              </Col>
            </Row>

            <AnalyticsTable
              tableData={tableConfig.tableData}
              isDescriptionTable={tableConfig.isDescriptionTable}
              isTableLoading={tableConfig.isTableLoading}
              emptyLines={tableConfig.emptyLines}
              tableColumns={tableConfig.tableColumns}
              noScroll={tableConfig.noScroll}
              onChange={handleTableChange}
            />
          </Fragment>
        )
      }
    </Fragment>
  );
};

UserFeedbackPerResponseSection.propTypes = {
  data: PropTypes.array,
  sectionTitle: PropTypes.string,
  seriesName: PropTypes.string,
  isLoading: PropTypes.bool,
  renderOnlyTable: PropTypes.bool
};

export default UserFeedbackPerResponseSection;
